/* ---------------------------------------------------------------------------
   "Record work" — the construction entry, made standing on the site.

   Proposal 1 §3.2: site staff record updates as they are made, and "activities
   are selected from a prepared library ... so that an entry takes little time
   on site." This is the screen that promise lives or dies on.

   THE BRIEF IS THREE TAPS — where, what, camera — and every decision below is
   subordinate to it. Concretely: the activity picker is the first control and
   the largest; the quantity box appears only for an activity measured in
   something; the note, the element and the component are all optional and are
   drawn after the shutter. An entry that records only "concrete poured, pier 2,
   today, here is the photograph" is a complete entry and the sheet says so by
   accepting it without complaint.

   WHY THE ACTIVITY IS A GROUPED LIST AND NOT A `<select>`. The same finding the
   defect dialog's component picker records: a dropdown of twenty-nine entries on
   a phone is a scroll wheel somebody thumbs past in sunlight. The groups are the
   stages of the work in the order it is done — a crew that has just struck
   formwork finds "Formwork struck" under the same heading as the pour it
   followed — and they are the library's own ordering, not this file's.

   WHAT IS DELIBERATELY NOT ASKED. No start and finish time, no labour, no
   plant, no weather. Those belong on a daily site return, which is a different
   record written once a day whether or not anything was built. Asking for them
   here would make every pour cost a form.

   AND NO UNIT. The library holds the unit for each activity, the endpoint looks
   it up, and a caller naming a different one is refused — so there is nothing
   for this sheet to offer and no way for it to be got wrong. The box prints the
   unit as a suffix so the person typing knows what is wanted.

   Rendered into src/main.ts's dialog layer beside the others, on the same "no
   lifecycle of its own" basis.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, type ProgressDraft, type State } from '../core/store.ts';
import { approxKb, downscale } from '../core/downscale.ts';
import { button, field, scrim } from '../components/widgets.ts';
import { findBridge } from '../data/derive.ts';
import { activitiesByGroup, activityById, activityUnit } from '../data/activities.ts';
import { siteElementGroups } from '../data/elements.ts';
import { componentChoices, componentTrail } from '../data/componentTree.ts';
import { defaultActivityForKind } from '../data/activityKinds.ts';
import { addDays, isIsoDay } from '../data/periods.ts';
import { forecastOf } from './programme.ts';
import { baselineComponents } from '../data/programme.ts';

/**
 * Whether moving from one draft to the next changes anything this sheet draws
 * beyond the control the change came from.
 *
 * The activity does: it lights a row, names the entry in the heading, and
 * decides whether the quantity box exists at all. So does the photograph —
 * nothing but a re-render puts the thumbnail on screen in place of the shutter.
 * So does the component, which is echoed by a breadcrumb rather than by the
 * control that sets it.
 *
 * So does the date, once it is a whole day: what the programme had on that day
 * is what the planned list offers (QA 2026-09-30).
 *
 * The quantity and the note are not. Each is drawn by nothing but its own
 * input, which already holds what was typed, so typing into them costs no
 * render at all — the rule every dialog here keeps.
 */
export function progressDialogNeedsPaint(a: ProgressDraft, b: ProgressDraft): boolean {
  return (
    a.activity !== b.activity ||
    a.photo !== b.photo ||
    a.component !== b.component ||
    (a.occurredOn !== b.occurredOn && isIsoDay(b.occurredOn))
  );
}

/** What the sheet is headed. */
function progressTitle(s: Readonly<State>): string {
  return activityById(s.progressDraft.activity)?.label ?? 'Record work';
}

/** The sheet, or null when it is closed. */
export function progressDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.progressBridgeId) return null;
  const b = findBridge(s.progressBridgeId);
  /* No record to hang this on, which happens only where the register is empty.
     Closed rather than drawn over nothing — the answer every other dialog here
     gives when the thing it was opened for has gone. */
  if (b === null) return null;

  const d = s.progressDraft;
  const chosen = activityById(d.activity);
  const unit = activityUnit(d.activity);

  const setField = (key: keyof ProgressDraft) => (e: Event) =>
    actions.setProgressField(
      key,
      (e.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value,
    );

  const form = h(
      'form',
      {
        class: 'dlg__panel dlg__panel--sm',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': `Record work — ${b.name}`,
        onsubmit: (e: Event) => {
          e.preventDefault();
          void actions.saveProgress();
        },
      },

      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: `${b.id} · construction` }),
          /* The heading names the activity once one is chosen, so somebody who
             has scrolled past the picker can still see what they are recording
             without scrolling back. */
          h('h2', { class: 'dlg__title', text: progressTitle(s) }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Cancel',
            'aria-label': 'Cancel',
            onclick: () => actions.closeRecordProgress(),
          },
          '×',
        ),
      ),

      s.progressError
        ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.progressError }))
        : null,

      h(
        'div',
        { class: 'dlg__body' },

        /* WHAT THE PROGRAMME HAS ON TODAY AND THIS WEEK, first (LC-45): one tap
           fills where and what, from the accepted baseline's forecast. */
        plannedPicker(s, d),

        /* FIRST AND FULL WIDTH. It is the one control that must be reachable
           without reading anything else on the sheet. */
        activityPicker(d),

        h(
          'div',
          { class: 'dlg__form-grid' },

          field(
            'Done on',
            h('input', {
              id: 'prg-date',
              class: 'input',
              type: 'date',
              required: true,
              value: d.occurredOn,
              oninput: setField('occurredOn'),
            }),
            'prg-date',
          ),

          /* THE BOX ONLY EXISTS WHERE THE ACTIVITY IS MEASURED IN SOMETHING.
             Twelve of the twenty-nine activities take no quantity, and offering
             an empty box against "Curing complete" invites a number nobody can
             interpret — which is exactly what the endpoint refuses. Drawn as a
             disabled note rather than omitted entirely, so the sheet does not
             appear to lose a control between one activity and the next. */
          unit !== null
            ? field(
                `How much (${unit})`,
                h('input', {
                  id: 'prg-qty',
                  class: 'input',
                  type: 'number',
                  min: '0',
                  step: 'any',
                  inputmode: 'decimal',
                  placeholder: 'Optional',
                  value: d.quantity,
                  oninput: setField('quantity'),
                }),
                'prg-qty',
              )
            : field(
                'How much',
                h('input', {
                  id: 'prg-qty',
                  class: 'input',
                  disabled: true,
                  value: '',
                  placeholder: chosen
                    ? `${chosen.label} is not measured in anything`
                    : 'Choose what was done first',
                }),
                'prg-qty',
              ),

          h('div', { style: 'grid-column: 1 / -1' }, photoField(d)),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Where on the structure',
              h(
                'select',
                { id: 'prg-element', class: 'select', onchange: setField('element') },
                /* The empty option is FIRST and is not a blank line: "did not
                   say" is a real answer and the picker names it, rather than
                   leaving an unlabelled row that reads as a rendering fault. */
                h('option', { value: '', selected: d.element === '', text: 'Not recorded' }),
                ...siteElementOptions(d.element),
              ),
              'prg-element',
            ),
          ),

          h('div', { style: 'grid-column: 1 / -1' }, componentField(s, d)),

          h(
            'div',
            { style: 'grid-column: 1 / -1' },
            field(
              'Note',
              h('textarea', {
                id: 'prg-note',
                class: 'input',
                rows: 3,
                placeholder: chosen?.note ?? 'Anything the activity and the quantity do not say',
                value: d.note,
                oninput: setField('note'),
              }),
              'prg-note',
            ),
          ),
        ),
      ),

      h(
        'div',
        { class: 'dlg__foot' },
        h('span', { class: 'spacer' }),
        button('Cancel', { onClick: () => actions.closeRecordProgress() }),
        button(s.progressSaving ? 'Recording…' : 'Record', {
          primary: true,
          type: 'submit',
          disabled: s.progressSaving,
        }),
      ),
    );
  return h(
    'div',
    {
      class: 'dlg',
      ...scrim(() => actions.closeRecordProgress(), () => s.progressSaving),
    },
    form,
  );
}

/** The elements under the form's headings, as options (QA 2026-09-30). */
export function siteElementOptions(selected: string): HTMLElement[] {
  return siteElementGroups().map((g) =>
    h(
      'optgroup',
      { label: g.group },
      ...g.elements.map((el) => h('option', { value: el.id, selected: el.id === selected, text: el.label })),
    ),
  );
}

/* --------------------------- what was planned --------------------------- */

/** How many planned activities the sheet offers before the library. */
const PLANNED_SHOWN = 8;

/** One planned activity on offer: what it is, where, and when it is forecast. */
export interface PlannedChoice {
  activityId: string;
  name: string;
  component: string;
  componentId: string | null;
  activity: string | null;
  when: 'today' | 'week';
  from: string;
  to: string;
}

/**
 * The activities the programme has under way today, then those due to be under
 * way in the rest of this week (to Sunday), from the accepted baseline's
 * forecast — so an activity running late is still offered on the day it is
 * actually being done. Matched activities only: an unmatched one names no
 * component to record against. Not complete, and never more than eight.
 */
export function plannedChoices(s: Readonly<State>, today: string = new Date().toISOString().slice(0, 10)): PlannedChoice[] {
  if (s.baseline === null || s.progressBridgeId !== s.selectedId) return [];
  const forecast = forecastOf(s, today);
  if (forecast === null) return [];
  const weekday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7;
  const sunday = addDays(today, 6 - weekday);
  const matches = new Map(s.matches.map((m) => [m.activityId, m]));
  const out: PlannedChoice[] = [];
  for (const r of forecast.rows) {
    const m = matches.get(r.activityId);
    if (!m || r.state === 'complete') continue;
    const when = r.forecastStart <= today && r.forecastFinish >= today
      ? 'today'
      : r.forecastStart > today && r.forecastStart <= sunday
        ? 'week'
        : null;
    if (when === null) continue;
    out.push({
      activityId: r.activityId,
      name: r.name,
      component: m.component,
      componentId: m.componentId ?? null,
      activity: defaultActivityForKind(r.kind, r.name),
      when,
      from: r.forecastStart,
      to: r.forecastFinish,
    });
  }
  return out
    .sort((a, b) => (a.when === b.when ? a.from.localeCompare(b.from) : a.when === 'today' ? -1 : 1))
    .slice(0, PLANNED_SHOWN);
}

function plannedPicker(s: Readonly<State>, d: ProgressDraft): HTMLElement | null {
  /* On the day the work was done, which is not today for an entry made later. */
  const choices = isIsoDay(d.occurredOn) ? plannedChoices(s, d.occurredOn) : plannedChoices(s);
  if (choices.length === 0) return null;
  return h(
    'div',
    { class: 'dlg__block' },
    field(
      d.occurredOn && d.occurredOn !== new Date().toISOString().slice(0, 10) ? 'Planned that day and that week' : 'Planned today and this week',
      h(
        'div',
        { class: 'actpick', role: 'radiogroup', 'aria-label': 'Planned today and this week', 'data-planned': String(choices.length) },
        ...choices.map((c) => {
          const on = d.component === c.component && (c.activity === null || d.activity === c.activity);
          return h(
            'button',
            {
              type: 'button',
              class: ['actpick__row', on && 'actpick__row--on'],
              role: 'radio',
              'aria-checked': on ? 'true' : 'false',
              'data-activity': c.activityId,
              onclick: () => actions.pickPlannedActivity(c.component, c.componentId, c.activity),
            },
            h('span', { class: 'actpick__label', text: `${c.name} · ${c.component}` }),
            h('span', { class: 'actpick__unit', text: c.when === 'today' ? (isIsoDay(d.occurredOn) && d.occurredOn !== new Date().toISOString().slice(0, 10) ? 'that day' : 'today') : c.from.slice(5) }),
          );
        }),
      ),
    ),
  );
}

/* --------------------------- what was done --------------------------- */

/**
 * The library, grouped, as rows big enough to hit with a thumb.
 *
 * Every entry is drawn — there is no search box and no "more" fold. Twenty-nine
 * is deliberately few enough not to need one, and a library long enough to need
 * searching is a library that has stopped saving time on site (see the header
 * of src/data/activities.ts). If it ever grows past what fits, the answer is to
 * question the twenty-ninth entry, not to add a filter.
 *
 * The four entries under "Checks and events" are not work — hold point, survey
 * check, non-conformance, work stopped — and they are what make this a
 * construction record rather than a production tally. They are drawn in the
 * same list, at the end, because a crew that had to stop should not have to
 * find a different screen to say so.
 */
function activityPicker(d: ProgressDraft): HTMLElement {
  return h(
    'div',
    { class: 'dlg__block' },
    field(
      'What was done',
      h(
        'div',
        { class: 'actpick', role: 'radiogroup', 'aria-label': 'What was done' },
        ...activitiesByGroup().flatMap((group) => [
          h('div', { class: 'actpick__group', text: group.group }),
          ...group.activities.map((a) =>
            h(
              'button',
              {
                type: 'button',
                class: ['actpick__row', a.id === d.activity && 'actpick__row--on'],
                role: 'radio',
                'aria-checked': a.id === d.activity ? 'true' : 'false',
                onclick: () => actions.setProgressActivity(a.id),
              },
              h('span', { class: 'actpick__label', text: a.label }),
              /* The unit, where there is one, stated on the row rather than
                 discovered after choosing. Somebody looking for the entry to
                 record forty cubic metres against can see which ones take a
                 number. */
              a.unit ? h('span', { class: 'actpick__unit', text: a.unit }) : null,
            ),
          ),
        ]),
      ),
    ),
  );
}

/* --------------------------- where on the model --------------------------- */

/**
 * The component, from the live viewport's own tree.
 *
 * OPTIONAL AND SECONDARY, which is the difference between this and the defect
 * dialog's picker. A defect whose component is not recorded is a defect nobody
 * can find again; a pour is a volume over a period against a part of the
 * structure the element field already names, and the model is a convenience on
 * top of that. So this is a `<select>` of the leaves rather than a tree, and it
 * is drawn below the element rather than instead of it.
 *
 * Absent entirely where the viewport has not reported a tree — on a structure
 * with no model, or before one has drawn. The sheet does not explain itself in
 * that case: a control that is not there needs no apology, and the element
 * field above has already asked the question that matters.
 */
function componentField(s: Readonly<State>, d: ProgressDraft): HTMLElement | null {
  const tree = s.heroComponentTree ?? [];

  /* The same flat list the Defect Inspector's one-`<select>` picker uses, in
     the tree's own order — so the parts of one pier stay together and the list
     reads as a walk over the structure rather than an alphabet. ONE ROW PER PART
     where the model has ids (LC-14): every pile group's "Pile-3" is its own
     choice, and the entry stores the id of the one picked beside its name.
     Where the 3D view has not drawn on this device, the components the
     programme says it builds (QA 2026-09-30): work recorded without them
     counted towards nothing. */
  const choices =
    tree.length > 0
      ? componentChoices(tree)
      : s.baseline !== null && s.progressBridgeId === s.selectedId
        ? baselineComponents(s.baseline.activities)
        : [];
  if (choices.length === 0) return null;
  const keyOf = (c: { id: string | null; name: string }): string => c.id ?? `name:${c.name}`;
  const chosen = d.componentId
    ? choices.find((c) => c.id === d.componentId)
    : choices.find((c) => c.name === d.component);

  return field(
    tree.length > 0 ? 'Component, from the 3D model' : 'Component, from the programme',
    h(
      'div',
      {},
      h(
        'select',
        {
          id: 'prg-component',
          class: 'select',
          onchange: (e: Event) => {
            const key = (e.target as HTMLSelectElement).value;
            const c = choices.find((x) => keyOf(x) === key);
            actions.setProgressComponent(c?.name ?? '', c?.id ?? null);
          },
        },
        h('option', { value: '', selected: d.component === '', text: 'Not recorded' }),
        ...choices.map((c) =>
          h('option', {
            value: keyOf(c),
            selected: c === chosen,
            text: c.text,
          }),
        ),
      ),
      /* The qualified name is space-joined and reads as one long phrase on a
         deep node, so the trail splits it back on the tree that produced it. */
      d.component
        ? h('div', {
            class: 'dlg__hint',
            text: componentTrail(tree, d.component).join(' › ') || d.component,
          })
        : null,
    ),
    'prg-component',
  );
}

/* --------------------------- the photograph --------------------------- */

/**
 * One photograph, straight off the rear camera.
 *
 * `capture: 'environment'` so the shutter opens rather than the gallery — the
 * whole design brief is that this is taken at the moment the work is done. The
 * same strip, classes and downscale the defect dialog uses: two sheets asking
 * for the same thing should not look like two different features.
 *
 * ONE, not several. An entry is one operation in one place; several
 * photographs of a structure on one visit is an inspection, and that is the
 * form that takes eight. The column is singular for the same reason.
 */
function photoField(d: ProgressDraft): HTMLElement {
  const input = h('input', {
    type: 'file',
    accept: 'image/*',
    capture: 'environment',
    class: 'visually-hidden',
    onchange: (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      // Cleared so picking the same file twice in a row still fires a change.
      target.value = '';
      if (!file) return;
      void downscale(file).then(
        (shrunk) => actions.setProgressPhoto(shrunk.dataUrl),
        (err: unknown) =>
          actions.setProgressError(
            err instanceof Error ? err.message : 'That file could not be read.',
          ),
      );
    },
  });

  return field(
    'Photograph',
    h(
      'div',
      {},
      h(
        'div',
        { class: 'insp-photos' },
        d.photo
          ? h(
              'span',
              { class: 'insp-photos__item' },
              h('img', {
                class: 'insp-photos__img',
                src: d.photo,
                alt: 'The photograph attached to this entry',
              }),
              h('span', { class: 'insp-photos__size', text: `${approxKb(d.photo)} kB` }),
              h('button', {
                type: 'button',
                class: 'imgslot__clear',
                title: 'Remove this photograph',
                'aria-label': 'Remove this photograph',
                text: '✕',
                onclick: () => actions.setProgressPhoto(null),
              }),
            )
          : h(
              'label',
              { class: 'insp-photos__add' },
              input,
              h('span', { text: 'Take a photograph' }),
            ),
      ),
    ),
  );
}
